/**
 * JSON-LD structured data.
 *
 * Tells search engines what this page is about in a form they parse exactly,
 * rather than inferring. Two types are declared:
 *   - SoftwareApplication, which is what can produce a rich result for an app
 *   - Organization, which links the product to BuildPassing and to the
 *     Instagram account, so the two are understood as the same entity
 *
 * The FAQ is deliberately NOT marked up as FAQPage: Google restricted FAQ rich
 * results to government and health sites in 2023, so the markup would add
 * weight for no benefit.
 */
import { useI18n } from '../i18n';
import { COMPANY, PRODUCT, SITE_ORIGIN } from '../lib/constants';

export function StructuredData() {
  const { t, locale } = useI18n();

  const data = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'SoftwareApplication',
        name: PRODUCT.name,
        url: SITE_ORIGIN,
        description: t.meta.description,
        applicationCategory: 'LifestyleApplication',
        operatingSystem: 'iOS, Android',
        inLanguage: locale,
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: locale === 'es' ? 'MXN' : 'USD',
          /*
           * This is the one description on the site a machine reads rather
           * than a person, and it said "A paid Pro tier is also available."
           * There is no paid tier, so Google was being told about a product
           * that does not exist — in the one place nobody would think to
           * proof-read.
           */
          description: 'Free. Every feature, for everyone. No account required.',
        },
        /* The app is about alcoholic drinks; both stores rate it accordingly. */
        contentRating: 'Mature 17+',
        publisher: { '@id': `${COMPANY.url}/#organization` },
      },
      {
        '@type': 'Organization',
        '@id': `${COMPANY.url}/#organization`,
        name: COMPANY.tradeName,
        url: COMPANY.url,
        email: COMPANY.email,
        sameAs: [PRODUCT.instagramUrl],
      },
    ],
  };

  return (
    <script
      type="application/ld+json"
      // The content is our own constant object, not anything a visitor can
      // influence, so there is no injection surface here.
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}
